// Types for Delivery Service Invoice editing
// Based on openapi.json EditInvoiceRequest schema and delivery detail responses

export enum DeliveryInvoiceStatus {
  PENDING = 'PENDING',
  CONFIRMED = 'CONFIRMED',
  PAID = 'PAID',
  CANCELLED = 'CANCELLED',
  DRAFT = 'DRAFT', // Legacy status, might appear in older records
}

export interface DeliveryInvoiceBillingItem {
  billingItemId?: string | null; // Required for updates/deletes, optional for new items (validated on backend as UUID)
  code: string; // Required - billing item code (e.g., 'PARKING', 'TOLL')
  description?: string | null;
  amount: number; // Required - negative values are credits (ZOHO-1831)
  isAutoCalculated?: boolean; // Default false - true for system-calculated items (Fuel Surcharge, etc.)
}

// Request type for editing delivery invoice
export interface DeliveryInvoiceEditRequest {
  addBillingItems?: DeliveryInvoiceBillingItem[]; // New billing items to add
  updateBillingItems?: DeliveryInvoiceBillingItem[]; // Existing items to update (requires billingItemId)
  removeBillingItemIds?: string[]; // Array of billing item IDs to remove
  status?: DeliveryInvoiceStatus; // Optional status update
}

// Response type for delivery invoice details
// This should match the actual API response (may differ from openapi if API returns more data)
export interface DeliveryInvoiceDetailResponse {
  invoiceId: string;
  invoiceNumber: string;
  invoiceStatus: DeliveryInvoiceStatus;
  workOrderId: string;
  workOrderNumber: string;
  
  // Client information (read-only in edit view)
  clientId: string;
  clientName: string;
  clientContactName: string;
  caseName?: string | null;
  fileReferenceNumber?: string | null;
  
  // Service person information (read-only)
  personServed?: string | null;
  attorneyName?: string | null;
  contactName?: string | null;
  
  // Billing information
  billingDate: string; // ISO date string
  billingItems: DeliveryInvoiceBillingItem[];
  totalBillingAmount: number;
  
  // Audit information
  createdAt: string;
  createdBy?: string | null;
  modifiedAt?: string | null;
  modifiedBy?: string | null;
}

// Simplified type for billing item used in UI table
export interface BillingItemForEdit {
  id: string; // UI-only unique ID for React keys
  billingItemId?: string | null; // Server ID - null for new items
  itemId: string; // The code of the billing item (for searching)
  code: string; // Display code
  description: string;
  amount: number;
  isAutoCalculated?: boolean;
}

// Response from edit invoice API
export interface DeliveryInvoiceEditResponse {
  success: boolean;
  message?: string;
  invoice?: {
    invoiceNumber: string;
    totalBillingAmount: number;
  };
}
